import {
    createTangramWxTag,
    getTangramInvokeInfo,
    tangramInvoke,
} from '@baidu/tangram-share-sdk';
import {EventEmitter} from 'events';
import {os} from './env';
import {isHarmonyOS} from './tools';

interface IInvokeAppOptions {
    invokeInfoParams?: object;
    actKey?: string;
    appName?: string;
}

interface WxConfType {
    wxDom: HTMLElement;
    onClick?: () => void;
    onSucceed?: (e: Event) => void;
    onFailed?: (e: Event) => void;
}

interface CreateTangramWxTagType {
    wxConf: WxConfType;
    sceneName: string;
    debug?: boolean;
    index?: number;
}

interface IWxInfo {
    appId: string;
    timestamp: number;
    nonceStr: string;
    signature: number;
}
interface IShareData {
    /* 分享标题 */
    title: string;
    /* 分享描述 */
    desc: string;
    /* 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致 */
    link: string;
    /* 分享图标 */
    imgUrl: string;
    /* 二次分享成功回调 */
    success: () => void;
}

type StringOrObject = string | Record<string, string>;
type ArrayOrStringOrObject = StringOrObject | StringOrObject[];
interface IInvokeInfoParams {
    [key: string]: ArrayOrStringOrObject;
};

export class UgInvokeApp {
    [key: string]: any;
    constructor(options?: IInvokeAppOptions) {
        this.appName = options?.appName || 'newapp';
        this.actKey = options?.actKey || '4a84a99c6c';
        this.renderWxTagFlag = false;
        this.invokeInfoParams = options?.invokeInfoParams || {};
        this.invokeInfo = null;
        this.emitter = new EventEmitter();
        this.shareData = null; // 微信二次分享数据
    }
    // 初始化wx-js-sdk
    private onLoadWxJSSDK(wxInfo: IWxInfo) {
        // @ts-ignore 检测是否已经引入微信JS-SDK
        if (typeof window.wx !== 'object') {
            const scriptTag = document.createElement('script');
            scriptTag.setAttribute('src', '//res.wx.qq.com/open/js/jweixin-1.6.0.js');
            scriptTag.setAttribute('type', 'text/javascript');
            // 标签加载完毕之后，设置微信配置
            scriptTag.onload = () => {
                // @ts-ignore 当前不是微信 或 wxjssdk引入失败 直接返回
                if (!wxInfo || typeof window.wx !== 'object') {
                    return;
                }
                // @ts-ignore
                wxInfo && window.wx.config?.({
                    debug: false,
                    appId: wxInfo?.appId,
                    timestamp: wxInfo?.timestamp,
                    nonceStr: wxInfo?.nonceStr,
                    signature: wxInfo?.signature,
                    jsApiList: [
                        'onMenuShareWeibo',
                        'onMenuShareTimeline', // 分享到朋友圈
                        'onMenuShareAppMessage', // 分享给好友
                        'onMenuShareQZone', // 分享到QQ空间
                        'onMenuShareQQ', // 分享到QQ好友
                        'updateAppMessageShareData', // 新版分享给好友
                        'updateTimelineShareData', // 新版分享朋友圈
                    ],
                    openTagList: ['wx-open-launch-app'], // 开放标签
                });
                // @ts-ignore
                window.wx.ready?.(() => {
                    const shareData = this.shareData;
                    // @ts-ignore
                    shareData && window?.wx?.onMenuShareTimeline?.(shareData);
                    // @ts-ignore
                    shareData && window?.wx?.onMenuShareAppMessage?.(shareData);
                });
            };
            scriptTag.onerror = () => {
                console.error('[ug-invoke-app] 微信JS-SDK加载失败');
            };
            document.querySelector('head')?.appendChild(scriptTag);
        }
        else {
            console.info('[ug-invoke-app] 检测到微信JS-SDK已被引入，请确保SDK版本大于等于1.6.0');
        }
    }
    // 获取当前调起位的调起配置
    private getSceneInvokeConf(sceneName: string, index = 0) {
        const sceneInvokeConf = this.invokeInfo?.[sceneName]?.[index];
        return sceneInvokeConf ?? {};
    }
    // 异步获取调起位list信息
    getInvokeInfo(invokeInfoParams: IInvokeInfoParams) {
        const {pathname, search} = window.location;
        const isIOS = os.ios;
        getTangramInvokeInfo({
            appName: this.appName,
            actKey: this.actKey,
            shareUrl: `https://yiyanapp.baidu.com${pathname + search}`,
            // @ts-ignore 此配置项开启之后，invokeinfo的调起位配置可以同时支持数组和对象的数据格式
            isMultiParamType: true,
            invokeInfo: invokeInfoParams,
        }).then(res => {
            this.invokeInfo = res?.data?.invokeInfo;
            this.emitter.emit('invokeInfoReady');
            const createWxOpenTag = res?.data?.wxInfo || false;
            // load 微信 JSSDK
            createWxOpenTag && this.onLoadWxJSSDK(createWxOpenTag as any as IWxInfo);
            // 安卓微信 渲染微信开放标签
            if (createWxOpenTag && !isIOS) {
                this.renderWxTagFlag = true;
                this.emitter.emit('wxOpenTagReady');
            }
        });
    }
    // ulink/scheme调起
    execInvoke(sceneName: string, index?: number) {
        if (this.invokeInfo) {
            const sceneInvokeConf = this.getSceneInvokeConf(sceneName, index);
            sceneName && tangramInvoke({
                appName: this.appName,
                sceneName,
                sceneInvokeConf,
            });
        }
        else {
            this.emitter.on('invokeInfoReady', () => {
                const sceneInvokeConf = this.getSceneInvokeConf(sceneName, index);
                sceneName && tangramInvoke({
                    appName: this.appName,
                    sceneName,
                    sceneInvokeConf,
                });
            });
        }
    }

    // 微信开放标签渲染
    renderWxTag(tagInfo: CreateTangramWxTagType) {
        if (isHarmonyOS && tagInfo?.wxConf) {
            const fn = tagInfo?.wxConf?.onFailed;
            tagInfo.wxConf.onFailed = (e: Event) => {
                fn?.(e);
                // eslint-disable-next-line xss/no-location-href-assign
                window.location.href = 'https://appgallery.huawei.com/app/C108628119';
            };
        }
        if (this.renderWxTagFlag) {
            const sceneInvokeConf = this.getSceneInvokeConf(tagInfo.sceneName, tagInfo?.index);
            createTangramWxTag({
                ...tagInfo,
                sceneInvokeConf,
            });
        }
        else {
            this.emitter.on('wxOpenTagReady', () => {
                const sceneInvokeConf = this.getSceneInvokeConf(tagInfo.sceneName, tagInfo?.index);
                createTangramWxTag({
                    ...tagInfo,
                    sceneInvokeConf,
                });
            });
        }
    }
    // 微信二次分享
    setShareData(shareData: IShareData) {
        this.shareData = shareData;
    }
}
